import type { BaseConfig, CookieConfig } from './types';

// =============================================================================
// Deep Merge
// =============================================================================

export function deepMerge<T extends BaseConfig>(base: T, override?: Partial<T>): T {
  const out = Object.assign({}, base, override) as T;
  out.cookie = Object.assign({}, base.cookie, (override && override.cookie) || {}) as CookieConfig;
  return out;
}

// =============================================================================
// Cookie Functions
// =============================================================================

export function getCookie(key: string): string | null {
  try {
    return document.cookie.split('; ').reduce<string | null>((acc, cookie) => {
      const keyValue = cookie.split('=');
      return keyValue[0] === key ? keyValue[1] : acc;
    }, null);
  } catch {
    return null;
  }
}

export function setCookie(
  key: string,
  value: string,
  cookieConfig: CookieConfig,
  hoursExpire?: number,
  path?: string,
  domain?: string
): void {
  const expires = new Date(
    Date.now() + (hoursExpire || cookieConfig.hoursExpire) * 3600000
  ).toUTCString();
  document.cookie =
    key +
    '=' +
    value +
    ';expires=' +
    expires +
    ';domain=' +
    (domain || cookieConfig.domain) +
    ';path=' +
    (path || cookieConfig.path) +
    ';';
}

// =============================================================================
// Own Tracking Functions
// =============================================================================

export function getStashedUserEmail(isProduction: boolean): string | null {
  if (!isProduction) {
    return null;
  }

  const emailFromCookie = getCookie('pw_stashed_email');
  if (emailFromCookie) {
    return emailFromCookie;
  }

  return null;
}

export function setStashedUserEmail(email: string, cookieConfig: CookieConfig, isProduction: boolean) {
  if (!isProduction) {
    return;
  }

  setCookie('pw_stashed_email', email, cookieConfig);
}

// =============================================================================
// GDPR / Tracking Checks
// =============================================================================

export function isConsentGranted(): boolean {
  const consentCookie = getCookie('cookie-message');
  return consentCookie === 'true' || consentCookie === '1';
}

export function isTrackingOn(isProduction: boolean, localStorageOptOutKey: string): boolean {
  if (!isProduction) {
    return false;
  }

  let turnOffTracking: string | null = null;
  try {
    turnOffTracking = localStorage.getItem(localStorageOptOutKey);
  } catch {
    // if localStorage blocked, assume tracking ON
    turnOffTracking = null;
  }

  const trackingOnValues = [null, '', '0', 'false'];
  return trackingOnValues.indexOf(turnOffTracking) !== -1;
}

// =============================================================================
// Default Configuration
// =============================================================================

export const DEFAULTS: BaseConfig = {
  cookie: {
    key: 'user_id',
    hoursExpire: 8760,
    path: '/',
    domain: '.pushwoosh.com',
  },
  isProduction: true,
  localStorageOptOutKey: 'pw_off_user_actions_tracking',
};
